import {
  getDownloadUrl,
  getPreviewSource,
  isOfficeDocument,
  isWordDocument,
} from "../../util/fileUrls";
import { handleFileDownload } from "../../util/downloadFile";
import { errorToast } from "../../util/toastNotifications";
import DocxPreview from "./DocxPreview";
import SpreadsheetPreview from "./SpreadsheetPreview";

const onDownloadClick = async (event, file) => {
  try {
    await handleFileDownload(event, file);
  } catch {
    errorToast(`Failed to download "${file.file_name}"`);
  }
};

export default function FilePreview({ file, onClose }) {
  if (!file) return null;
  if (!file.file_type && !file.file_name) return null;

  const rawSrc = getPreviewSource(file);
  const downloadUrl = getDownloadUrl(file);
  const isHeic =
    file.file_type === "image/heic" || file.file_type === "image/heif";
  const src =
    isHeic && rawSrc ? rawSrc.replace(/\.(heic|heif)$/i, ".jpg") : rawSrc;

  const wordDocument = isWordDocument(file);
  const officeDocument = isOfficeDocument(file);

  const spreadsheet =
    file.file_type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    file.file_type === "application/vnd.ms-excel" ||
    file.file_type === "text/csv" ||
    file.file_name?.toLowerCase().endsWith(".xlsx") ||
    file.file_name?.toLowerCase().endsWith(".xls") ||
    file.file_name?.toLowerCase().endsWith(".csv");

  const htmlSrc = getPreviewSource(file);

  return (
    <div className="modal-overlay flex-columns" onClick={onClose}>
      <div
        className={`modal-content${
          wordDocument ? " modal-content--document" : ""
        } flex-columns`}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={onClose}>
          ✕
        </button>

        {file.file_type?.startsWith("image/") && (
          <img src={src} alt={file.file_name} />
        )}

        {file.file_type?.startsWith("video/") && (
          <video src={src} controls width="100%" style={{ maxHeight: "600px" }}>
            Your browser does not support the video tag.
          </video>
        )}

        {file.file_type?.startsWith("audio/") && (
          <audio
            src={src}
            controls
            style={{ width: "100%", marginTop: "20px" }}
          >
            Your browser does not support the audio tag.
          </audio>
        )}

        {file.file_type === "application/pdf" && (
          <>
            <div>
              <a
                href={downloadUrl || src}
                onClick={(e) => onDownloadClick(e, file)}
              >
                Download PDF
              </a>
            </div>
            <iframe
              src={src}
              width="100%"
              height="600px"
              title={file.file_name}
              style={{ border: "none" }}
            />
          </>
        )}

        {file.file_type === "text/html" && (
          <>
            <div>
              <a
                href={downloadUrl || file.file_url}
                onClick={(e) => onDownloadClick(e, file)}
              >
                Download html
              </a>
            </div>
            <iframe
              src={htmlSrc}
              width="100%"
              height="600"
              title="HTML Preview"
              sandbox="allow-same-origin allow-scripts allow-forms"
            />
          </>
        )}

        {spreadsheet && (
          <>
            <div className="modal-document-download">
              <a href={downloadUrl} onClick={(e) => onDownloadClick(e, file)}>
                Download {file.file_name}
              </a>
            </div>

            <SpreadsheetPreview file={file} />
          </>
        )}

        {wordDocument && (
          <>
            <div className="modal-document-download">
              <a href={downloadUrl} onClick={(e) => onDownloadClick(e, file)}>
                Download {file.file_name}
              </a>
            </div>
            <DocxPreview file={file} />
          </>
        )}

        {officeDocument && !wordDocument && !spreadsheet && (
          <div style={{ padding: "20px", textAlign: "center" }}>
            <p>In-browser preview is not available for this file type.</p>
            <a href={downloadUrl} onClick={(e) => onDownloadClick(e, file)}>
              Download {file.file_name}
            </a>
          </div>
        )}

        {!file.file_type?.startsWith("image/") &&
          !file.file_type?.startsWith("video/") &&
          !file.file_type?.startsWith("audio/") &&
          file.file_type !== "application/pdf" &&
          file.file_type !== "text/html" &&
          !officeDocument && (
            <div style={{ padding: "20px", textAlign: "center" }}>
              <p>Preview not available for this file type.</p>
              <a
                href={downloadUrl || src}
                onClick={(e) => onDownloadClick(e, file)}
              >
                Download {file.file_name}
              </a>
            </div>
          )}
      </div>
    </div>
  );
}
